import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';

const SafetyChecklist = () => {
  const [checkedItems, setCheckedItems] = useState({});

  const safetyCategories = [
    {
      category: "Essential Gear",
      icon: "Backpack",
      items: [
        "Sturdy hiking boots with ankle support",
        "Rain jacket and waterproof pants",
        "Sun protection (hat, sunglasses, sunscreen SPF 50+)",
        "Insect repellent (DEET-based recommended)",
        "First aid kit with basic medications"
      ]
    },
    {
      category: "Navigation & Communication",
      icon: "Compass",
      items: [
        "Offline maps downloaded to mobile device",
        "Fully charged power bank (10,000mAh minimum)",
        "Emergency whistle",
        "Flashlight or headlamp with extra batteries",
        "Emergency contact numbers saved"
      ]
    },
    {
      category: "Food & Hydration",
      icon: "Droplet",
      items: [
        "Minimum 3 liters of water per person",
        "Water purification tablets or filter",
        "High-energy snacks (trail mix, energy bars)",
        "Packed lunch for full-day treks",
        "Reusable water bottle"
      ]
    },
    {
      category: "Health & Safety",
      icon: "Heart",
      items: [
        "Personal medications and prescriptions",
        "Anti-malaria prophylaxis (consult doctor)",
        "Motion sickness medication if needed",
        "Emergency medical information card",
        "Travel insurance documentation"
      ]
    }
  ];

  const handleCheckboxChange = (categoryIndex, itemIndex) => {
    const key = `${categoryIndex}-${itemIndex}`;
    setCheckedItems(prev => ({
      ...prev,
      [key]: !prev?.[key]
    }));
  };

  const getCategoryProgress = (categoryIndex) => {
    const category = safetyCategories?.[categoryIndex];
    const checkedCount = category?.items?.filter((_, itemIndex) => 
      checkedItems?.[`${categoryIndex}-${itemIndex}`]
    )?.length;
    return Math.round((checkedCount / category?.items?.length) * 100);
  };

  const getTotalProgress = () => {
    const totalItems = safetyCategories?.reduce((sum, cat) => sum + cat?.items?.length, 0);
    const checkedCount = Object.values(checkedItems)?.filter(Boolean)?.length;
    return Math.round((checkedCount / totalItems) * 100);
  };

  return (
    <div className="bg-[var(--color-card)] rounded-lg p-6 border border-[var(--color-border)]">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-[var(--color-primary)]">
            <Icon name="Shield" size={24} color="var(--color-primary-foreground)" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Safety Checklist</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">Prepare for your adventure</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-[var(--color-accent)]">{getTotalProgress()}%</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Complete</div>
        </div>
      </div>
      <div className="space-y-6">
        {safetyCategories?.map((category, categoryIndex) => {
          const progress = getCategoryProgress(categoryIndex);
          
          return (
            <div key={categoryIndex} className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name={category?.icon} size={18} color="var(--color-primary)" />
                  <h4 className="font-medium text-[var(--color-foreground)]">{category?.category}</h4>
                </div>
                <span className="text-sm text-[var(--color-muted-foreground)]">{progress}%</span>
              </div>
              <div className="w-full bg-[var(--color-muted)] rounded-full h-2 overflow-hidden">
                <div 
                  className="h-full bg-[var(--color-success)] transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="space-y-2 pl-2">
                {category?.items?.map((item, itemIndex) => (
                  <Checkbox
                    key={itemIndex}
                    label={item}
                    checked={checkedItems?.[`${categoryIndex}-${itemIndex}`] || false}
                    onChange={() => handleCheckboxChange(categoryIndex, itemIndex)}
                    size="sm"
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {getTotalProgress() === 100 && (
        <div className="mt-6 p-4 bg-[var(--color-success)] bg-opacity-10 border border-[var(--color-success)] rounded-lg flex items-center gap-3">
          <Icon name="CheckCircle2" size={24} color="var(--color-success)" />
          <div>
            <p className="font-medium text-[var(--color-success)]">You're all set!</p>
            <p className="text-sm text-[var(--color-foreground)]">Your safety checklist is complete</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SafetyChecklist;